/**
 * Owner: Masa Bayu
 * File: qx-writer-academic-spine.ts
 * Date:Time: 2026-10-16 07:14
 * Description: Academic book spines · Front/Main/Back · QXWriter profiles.
 */

import type { QXPublicationStructureKind } from '@qx/template-engine';

export type WriterMatterZone = 'cover' | 'front' | 'main' | 'back';

export type WriterBookSpineItem = {
  readonly kind: QXPublicationStructureKind;
  readonly label: string;
  readonly matter: WriterMatterZone;
  /** Core for academic completeness checks · not forced into every profile. */
  readonly core?: boolean;
};

/**
 * Academic Book — teras ilmiah umum (bukan semua 48 elemen induk).
 * Cover · Front Matter · Main · Back Matter.
 */
export const SPINE_ACADEMIC_BOOK: readonly WriterBookSpineItem[] = Object.freeze([
  { kind: 'cover', label: 'Kulit Hadapan', matter: 'cover', core: true },
  { kind: 'title-page', label: 'Halaman Separuh Tajuk', matter: 'front' },
  { kind: 'title-page', label: 'Halaman Judul', matter: 'front', core: true },
  { kind: 'copyright-page', label: 'Hak Cipta & Penerbitan', matter: 'front', core: true },
  { kind: 'section', label: 'Dedikasi', matter: 'front' },
  { kind: 'table-of-contents', label: 'Senarai Kandungan', matter: 'front', core: true },
  { kind: 'chapter-opening', label: 'Prakata', matter: 'front' },
  { kind: 'chapter', label: 'Pendahuluan', matter: 'main', core: true },
  { kind: 'chapter', label: 'Bab 1', matter: 'main', core: true },
  { kind: 'chapter', label: 'Bab 2', matter: 'main' },
  { kind: 'conclusion', label: 'Kesimpulan', matter: 'main', core: true },
  { kind: 'back-matter', label: 'Rujukan', matter: 'back', core: true },
  { kind: 'author-bio', label: 'Biodata Pengarang', matter: 'back' },
  { kind: 'back-matter', label: 'Kulit Belakang', matter: 'back' },
]);

/**
 * Research Monograph — IIUM-style bab kajian + rujukan/lampiran.
 */
export const SPINE_RESEARCH_MONOGRAPH: readonly WriterBookSpineItem[] = Object.freeze([
  { kind: 'cover', label: 'Kulit Hadapan', matter: 'cover', core: true },
  { kind: 'title-page', label: 'Halaman Judul', matter: 'front', core: true },
  { kind: 'copyright-page', label: 'Hak Cipta & Penerbitan', matter: 'front', core: true },
  { kind: 'table-of-contents', label: 'Senarai Kandungan', matter: 'front', core: true },
  { kind: 'section', label: 'Abstrak', matter: 'front' },
  { kind: 'chapter', label: 'Bab 1 Pengenalan', matter: 'main', core: true },
  { kind: 'chapter', label: 'Bab 2 Sorotan Literatur', matter: 'main', core: true },
  { kind: 'chapter', label: 'Bab 3 Metodologi', matter: 'main', core: true },
  { kind: 'chapter', label: 'Bab 4 Dapatan', matter: 'main', core: true },
  { kind: 'chapter', label: 'Bab 5 Perbincangan', matter: 'main', core: true },
  { kind: 'chapter', label: 'Bab 6 Cadangan', matter: 'main' },
  { kind: 'conclusion', label: 'Bab 7 Kesimpulan', matter: 'main', core: true },
  { kind: 'back-matter', label: 'Rujukan', matter: 'back', core: true },
  { kind: 'back-matter', label: 'Lampiran', matter: 'back' },
  { kind: 'author-bio', label: 'Biodata Pengarang', matter: 'back' },
  { kind: 'back-matter', label: 'Kulit Belakang', matter: 'back' },
]);

/**
 * Academic Textbook — objektif · bab · latihan · rumusan · rujukan.
 */
export const SPINE_ACADEMIC_TEXTBOOK: readonly WriterBookSpineItem[] = Object.freeze([
  { kind: 'cover', label: 'Kulit Hadapan', matter: 'cover', core: true },
  { kind: 'title-page', label: 'Halaman Judul', matter: 'front', core: true },
  { kind: 'copyright-page', label: 'Hak Cipta & Penerbitan', matter: 'front', core: true },
  { kind: 'table-of-contents', label: 'Senarai Kandungan', matter: 'front', core: true },
  { kind: 'chapter', label: 'Pengenalan', matter: 'main', core: true },
  { kind: 'chapter', label: 'Bab 1', matter: 'main', core: true },
  { kind: 'section', label: 'Objektif Pembelajaran', matter: 'main' },
  { kind: 'section', label: 'Contoh & Latihan', matter: 'main' },
  { kind: 'chapter', label: 'Bab 2', matter: 'main' },
  { kind: 'chapter', label: 'Bab 3', matter: 'main' },
  { kind: 'section', label: 'Rumusan', matter: 'main' },
  { kind: 'back-matter', label: 'Rujukan', matter: 'back', core: true },
  { kind: 'back-matter', label: 'Glosari', matter: 'back' },
  { kind: 'back-matter', label: 'Kulit Belakang', matter: 'back' },
]);

/**
 * Edited Academic Volume — bab penyumbang · biodata · rujukan.
 */
export const SPINE_EDITED_VOLUME: readonly WriterBookSpineItem[] = Object.freeze([
  { kind: 'cover', label: 'Kulit Hadapan', matter: 'cover', core: true },
  { kind: 'title-page', label: 'Halaman Judul', matter: 'front', core: true },
  { kind: 'copyright-page', label: 'Hak Cipta & Penerbitan', matter: 'front', core: true },
  { kind: 'table-of-contents', label: 'Senarai Kandungan', matter: 'front', core: true },
  { kind: 'chapter-opening', label: 'Prakata Editor', matter: 'front' },
  { kind: 'chapter', label: 'Bab 1', matter: 'main', core: true },
  { kind: 'chapter', label: 'Bab 2', matter: 'main', core: true },
  { kind: 'chapter', label: 'Bab 3', matter: 'main' },
  { kind: 'conclusion', label: 'Penutup', matter: 'main' },
  { kind: 'back-matter', label: 'Rujukan', matter: 'back', core: true },
  { kind: 'author-bio', label: 'Biodata Penyumbang', matter: 'back', core: true },
  { kind: 'back-matter', label: 'Kulit Belakang', matter: 'back' },
]);

/** All academic profiles · resolve tip spine without inventing labels. */
export const ACADEMIC_SPINE_PROFILES: readonly (readonly WriterBookSpineItem[])[] =
  Object.freeze([
    SPINE_ACADEMIC_BOOK,
    SPINE_RESEARCH_MONOGRAPH,
    SPINE_ACADEMIC_TEXTBOOK,
    SPINE_EDITED_VOLUME,
  ]);
